import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { cn } from "@/shared/lib/utils";
import { toast } from "sonner";
import { toastApiError } from "@/shared/lib/api-error";
import {
  useProductSearch,
  useReassignHoldingProduct,
} from "@/admin/hooks/use-products";
import type { Product } from "@/admin/types/products";

interface Props {
  holdingUuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful reassignment (e.g. to refetch the claim). */
  onReassigned?: () => void;
}

/**
 * Assign the correct product to a registration whose product is unknown
 * (registered against the tenant placeholder), or correct a wrong product.
 * Generic - any tenant with placeholder intake reuses this.
 */
export function IdentifyProductDialog({
  holdingUuid,
  open,
  onOpenChange,
  onReassigned,
}: Props) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<Product | null>(null);
  const [reason, setReason] = useState("");
  const [createWarranty, setCreateWarranty] = useState(false);

  const reassign = useReassignHoldingProduct(holdingUuid);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(handle);
  }, [query]);

  // Reset on close (an event, not an effect) so the next open starts fresh
  // without triggering cascading renders.
  function handleOpenChange(next: boolean) {
    if (!next) {
      setQuery("");
      setDebounced("");
      setSelected(null);
      setReason("");
      setCreateWarranty(false);
    }
    onOpenChange(next);
  }

  // Include variants so concrete leaves are searchable, then drop VARIANT_BASE
  // umbrellas - a holding registers against a concrete product/variant, and the
  // reassign endpoint rejects a variant base (you can't own "the family").
  const { data, isFetching } = useProductSearch(debounced.trim(), {
    include_variants: true,
  });
  const results = useMemo<Product[]>(
    () => (data?.results ?? []).filter((p) => p.product_type !== "VARIANT_BASE"),
    [data],
  );
  const trimmed = debounced.trim();
  const showEmpty =
    trimmed.length >= 2 && !isFetching && results.length === 0;

  function handleConfirm() {
    if (!selected) return;
    reassign.mutate(
      {
        product_uuid: selected.uuid,
        reason: reason.trim() || undefined,
        create_warranty: createWarranty,
      },
      {
        onSuccess: () => {
          toast.success("Product assigned to the registration.");
          handleOpenChange(false);
          onReassigned?.();
        },
        onError: (err) => toastApiError(err, "Failed to assign product"),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Identify product</DialogTitle>
          <DialogDescription>
            Search the catalogue and assign the correct product to this
            registration.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--ink-3)]"
              aria-hidden
            />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or SKU…"
              autoComplete="off"
              className="pl-8"
            />
            {isFetching && trimmed.length >= 2 && (
              <Loader2
                className="absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-[var(--ink-3)]"
                aria-hidden
              />
            )}
          </div>

          <div className="max-h-[260px] overflow-y-auto rounded-lg border-[0.5px] border-[var(--rule-strong)]">
            {trimmed.length < 2 ? (
              <p className="px-3 py-6 text-center text-[12px] text-[var(--ink-3)]">
                Type at least 2 characters to search.
              </p>
            ) : showEmpty ? (
              <p className="px-3 py-6 text-center text-[12px] text-[var(--ink-3)]">
                No matches for &ldquo;{trimmed}&rdquo;.
              </p>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {results.map((p) => {
                  const isSelected = selected?.uuid === p.uuid;
                  return (
                    <li key={p.uuid}>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setSelected(p)}
                        className={cn(
                          "h-auto w-full justify-start rounded-none px-3 py-2 text-left font-normal",
                          isSelected && "bg-[var(--bg-subtle)]",
                        )}
                      >
                        <span className="flex w-full items-center gap-2">
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-[13px] text-[var(--ink-1)]">
                              {p.cached_long_name || p.cached_short_name}
                            </span>
                            <span className="truncate text-[11px] text-[var(--ink-3)]">
                              {[p.product_template?.name, p.category?.name]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </span>
                          {isSelected && (
                            <Check className="ml-auto size-4 text-[var(--green)]" />
                          )}
                        </span>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reassign-reason">Reason (optional)</Label>
            <Input
              id="reassign-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Identified from photos / customer confirmation"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={createWarranty}
              onCheckedChange={(v) => setCreateWarranty(Boolean(v))}
            />
            <span className="text-[12px] text-[var(--ink-2)]">
              Also create the manufacturer warranty for the assigned product
            </span>
          </label>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={reassign.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected || reassign.isPending}
          >
            {reassign.isPending ? "Assigning…" : "Assign product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
